import { withDependencies, named } from '@wix/thunderbolt-ioc'
import {
	CarmiInstance,
	EditorFeatureConfigSymbol,
	ViewerManagerStructureAPISymbol,
	IPropsStore,
	Props,
	CurrentRouteInfoSymbol,
	ILogger,
	LoggerSymbol,
	IComponentStructureFixer,
	ComponentStructureFixerSymbol,
} from '@wix/thunderbolt-symbols'
import type { IViewerManagerImplementor, Component, ViewerAPI } from 'thunderbolt-viewer-manager-types'
import type { DSCarmi } from 'thunderbolt-ds-carmi-root'
import type { ICurrentRouteInfo } from 'feature-router'
import type { SospActivation, ViewerManagerStructureApiEditorConfig } from './types'
import { name } from './symbols'
import { toInflatedStructure } from './utils/structureUtils'
import { get, pick } from 'lodash'

export const StructureAPIImplementor = withDependencies(
	[ViewerManagerStructureAPISymbol],
	(structureAPI: ViewerAPI['structure']): IViewerManagerImplementor<'structure'> => ({
		structure: structureAPI,
	})
)

export const ViewerManagerStructureApi = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		CarmiInstance,
		Props,
		CurrentRouteInfoSymbol,
		LoggerSymbol,
		ComponentStructureFixerSymbol,
	],
	(
		{
			structure,
			repeatersData,
			pagesToShowSosp,
			displayedOnlyToRepeaterAncestor,
		}: ViewerManagerStructureApiEditorConfig,
		{ structure: structureApi }: DSCarmi,
		props: IPropsStore,
		currentRouteInfo: ICurrentRouteInfo,
		logger: ILogger,
		compStructureFixer: IComponentStructureFixer
	): ViewerAPI['structure'] => {
		const isSospActiveOnCurrentPage: SospActivation = () => {
			return currentRouteInfo.getCurrentRouteInfo()
				? pagesToShowSosp[currentRouteInfo.getCurrentRouteInfo()!.pageId]
				: false
		}

		return {
			getStructure: (id: string) =>
				toInflatedStructure(
					id,
					structure,
					repeatersData,
					props,
					isSospActiveOnCurrentPage,
					compStructureFixer,
					displayedOnlyToRepeaterAncestor
				),
			updateStructure: (id: string, value: Component | undefined) => {
				// TODO: DELETE LATER
				// try to analyse a reason for a HUGE STRIP bug: Jira: TB-6189
				// note: 50000 is the magic number, limit in DS for component height
				if (value && get(value, ['layout', 'height'], 0) > 50001) {
					const currentStructure = structure[id]
					const currentLayout = get(currentStructure, 'layout')

					// report only when it happens on the first time
					if (currentLayout && get(currentLayout, 'height', 0) < 50000) {
						logger.meter('Set HUGE height for component', {
							// for some reason customParams stores as null in BI, even it sent as expected.
							// workaround: use extra via paramsOverrides
							paramsOverrides: {
								extra: JSON.stringify({
									newLayout: value.layout,
									structure: pick(currentStructure, ['id', 'componentType', 'layout']),
								}),
							},
						})
					}
				}

				// Delete Component
				if (!value) {
					structureApi.setStructure(id)
					return
				}

				const { pageId, isPreset } = value.metaData
				const compStructure = { ...value, metaData: { pageId, isPreset } }
				compStructureFixer.fixStructureAndReport(structure[id], compStructure)
				structureApi.setStructure(id, compStructure)
			},
		}
	}
)
